import { PriorityStatusTooltip } from "@/components/priorities/PriorityStatusTooltip";
import { UserAvatar } from "@/components/UserAvatar";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import { reportService } from "@/services/reportService";
import { Priority, PriorityStatus, PriorityType } from "@/types";
import { RANKED_PPP_PARAMS, usePpps } from "@/hooks/usePpps";
import { useQuery } from "@tanstack/react-query";
import { AlertCircle, Crown, Loader2 } from "lucide-react";
import type { FieldArrayWithId } from "react-hook-form";
import { useFormContext, useWatch } from "react-hook-form";
import type { PriorityFormValues } from "./priority-form-schema";
import {
    avatarAssigneeClass,
    avatarAssigneeFallbackClass,
    avatarDriClass,
    avatarDriFallbackClass,
    DisplayUser,
    formatReportDate,
    PRIORITY_TYPE_LABEL,
    rollupStatusDisplay,
    rollupStatusDot,
    sectionKickerClass,
} from "./priority-form-constants";

interface PriorityFormSidebarProps {
    isEdit: boolean;
    priorityCode?: string;
    initialData?: Partial<Priority>;
    assignmentFields: FieldArrayWithId<PriorityFormValues, "assignments", "id">[];
    assigneeNameByPid: Map<string, string>;
    userByPid: Map<string, DisplayUser>;
}

export function PriorityFormSidebar({
    isEdit,
    priorityCode,
    initialData,
    assignmentFields,
    assigneeNameByPid,
    userByPid,
}: PriorityFormSidebarProps) {
    const form = useFormContext<PriorityFormValues>();
    const { user } = useAuth();

    const [wName, wWeek, wYear, wType, wPriorityStatus, wPppPids] = useWatch({
        control: form.control,
        name: ["name", "weekNumber", "weekYear", "type", "status", "pppPids"],
    });
    const watchedAssignments = useWatch({ control: form.control, name: "assignments" });

    // Same query the PPP picker uses, so this is a cache hit.
    const { data: pppData } = usePpps(RANKED_PPP_PARAMS);
    const pppNames = (pppData?.data ?? []).filter((p) => wPppPids?.includes(p.pid)).map((p) => p.name);
    const isCandidate = !wPppPids?.length || !watchedAssignments.some((a) => a.isDri);
    const typeKey = (wType ?? "SHP") as PriorityType;

    const driAssignment = watchedAssignments.find((a) => a.isDri);
    const driDisplayName = driAssignment ? (assigneeNameByPid.get(driAssignment.userPid) ?? "") : "";
    const driUserRow = driAssignment ? userByPid.get(driAssignment.userPid) : undefined;
    const driStatusKey = (isEdit ? driAssignment?.status ?? "queue" : "queue") as PriorityStatus;

    const nonDriAssignments = assignmentFields.filter((row) => {
        const watched = watchedAssignments.find((a) => a.userPid === row.userPid);
        return !watched?.isDri;
    });

    // People to notify = everyone except the creator (you don't notify yourself).
    const notifyFirstNames = watchedAssignments
        .filter((a) => a.userPid !== user?.pid)
        .map((a) => (assigneeNameByPid.get(a.userPid) ?? "").split(/\s+/)[0])
        .filter(Boolean);
    const notificationNames = notifyFirstNames.join(", ");

    const previewTitle = (wName ?? "").trim() || "Untitled priority";
    const previewKrLine = [
        isEdit && priorityCode ? priorityCode : null,
        pppNames.length > 0 ? pppNames.slice(0, 2).join(", ") + (pppNames.length > 2 ? ` +${pppNames.length - 2}` : "") : null,
        wWeek && wYear ? `W${wWeek} · ${wYear}` : null,
    ]
        .filter(Boolean)
        .join(" · ");

    const rollupKey = (wPriorityStatus ?? "queue") as PriorityStatus;
    const priorityPid = initialData?.pid;

    const { data: recentReportsData, isLoading: isLoadingRecentReports, isError: isRecentReportsError } = useQuery({
        queryKey: ["reports", "priority", "recent", priorityPid],
        queryFn: () => reportService.getReports(1, 3, { priorityPid: priorityPid as string }),
        enabled: isEdit && Boolean(priorityPid),
    });
    const recentReports = recentReportsData?.data ?? [];

    return (
        <aside className="lg:sticky lg:top-4 lg:self-start">
            <p className={cn(sectionKickerClass, "mb-2")}>Preview</p>

            <div className="overflow-hidden rounded-xl border border-border/80 bg-background shadow-sm">
                <div className="flex items-start justify-between gap-2 border-b border-border px-4 py-3">
                    <div className="min-w-0">
                        <div className="truncate text-sm font-semibold text-foreground">{previewTitle}</div>
                        <div className="mt-1 text-xs text-muted-foreground">{previewKrLine || "—"}</div>
                        {isCandidate && (
                            <span className="mt-1.5 inline-flex items-center rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
                                Candidate — needs a DRI and a PPP to rank
                            </span>
                        )}
                    </div>
                    <PriorityStatusTooltip status={rollupKey} className="shrink-0">
                        <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-border bg-muted/50 px-2 py-0.5 text-[10px] font-medium text-foreground">
                            <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", rollupStatusDot[rollupKey])} />
                            {rollupStatusDisplay[rollupKey].label}
                        </span>
                    </PriorityStatusTooltip>
                </div>

                {driAssignment ? (
                    <div className="flex items-center gap-2 border-b border-border px-4 py-2.5">
                        <UserAvatar
                            src={driUserRow?.avatarUrl}
                            name={driDisplayName || "DRI"}
                            className={avatarDriClass}
                            fallbackClassName={avatarDriFallbackClass}
                        />
                        <div className="min-w-0 flex-1 truncate text-[13px] font-medium text-foreground">
                            {driDisplayName || "DRI"}
                            <span className="ml-1.5 inline-flex items-center gap-1 rounded-md border border-primary/35 bg-primary/12 px-1.5 py-0.5 text-[11px] font-semibold tabular-nums text-primary">
                                <Crown className="h-3.5 w-3.5 shrink-0" aria-hidden />
                                DRI
                            </span>
                        </div>
                        <div className="line-clamp-2 min-w-0 flex-[2] text-[11px] italic text-muted-foreground">
                            {(driAssignment.icTask ?? "").trim() || "—"}
                        </div>
                        <span className="hidden shrink-0 rounded-full bg-muted px-2 py-0.5 text-[9px] text-muted-foreground sm:inline">
                            {rollupStatusDisplay[driStatusKey]?.label ?? driStatusKey}
                        </span>
                    </div>
                ) : null}

                {nonDriAssignments.map((row) => {
                    const name = assigneeNameByPid.get(row.userPid) ?? row.userPid;
                    const previewUser = userByPid.get(row.userPid);
                    const watched = watchedAssignments.find((a) => a.userPid === row.userPid);
                    const icTask = watched?.icTask ?? "";
                    const st = isEdit ? watched?.status ?? "queue" : "queue";
                    return (
                        <div key={row.id} className="flex items-center gap-2 border-b border-border px-4 py-2.5 last:border-b-0">
                            <UserAvatar
                                src={previewUser?.avatarUrl}
                                name={name}
                                className={avatarAssigneeClass}
                                fallbackClassName={avatarAssigneeFallbackClass}
                            />
                            <div className="min-w-0 flex-1 truncate text-[13px] font-medium text-foreground">{name}</div>
                            <div className="line-clamp-2 min-w-0 flex-[2] text-[11px] italic text-muted-foreground">
                                {icTask.trim() || "—"}
                            </div>
                            <span className="hidden shrink-0 rounded-full bg-muted px-2 py-0.5 text-[9px] text-muted-foreground sm:inline">
                                {rollupStatusDisplay[st as PriorityStatus]?.label ?? st}
                            </span>
                        </div>
                    );
                })}

                <div className="flex items-center gap-2 border-t border-border/50 bg-muted/40 px-4 py-2.5">
                    <span className="flex-1 text-xs text-muted-foreground">
                        {watchedAssignments.length} {watchedAssignments.length === 1 ? "person" : "people"}
                        {watchedAssignments.length > 0
                            ? isEdit
                                ? " · rollup follows assignee statuses"
                                : " · assignees default to Queue when created"
                            : ""}
                    </span>
                </div>
            </div>

            <div className="mt-3.5 overflow-hidden rounded-xl border border-border/80 bg-background shadow-sm">
                <div className="border-b border-border px-3.5 py-2.5 text-xs font-medium text-muted-foreground">
                    {isEdit ? "Recent reports" : "What happens after confirm"}
                </div>
                <div className="flex flex-col gap-2 px-3.5 py-2.5">
                    {isEdit ? (
                        isLoadingRecentReports ? (
                            <div className="flex items-center gap-2 py-1 text-xs text-muted-foreground">
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                Loading recent reports...
                            </div>
                        ) : isRecentReportsError ? (
                            <p className="text-xs text-muted-foreground">Could not load recent reports.</p>
                        ) : recentReports.length === 0 ? (
                            <p className="text-xs text-muted-foreground">No recent reports for this priority yet.</p>
                        ) : (
                            recentReports.map((report) => (
                                <div key={report.pid} className="rounded-md border border-border/70 bg-background/80 px-3 py-2.5">
                                    <div className="flex items-center gap-2">
                                        <UserAvatar
                                            src={report.user?.avatarUrl}
                                            name={report.user?.fullName || "User"}
                                            className="h-5 w-5"
                                            fallbackClassName="text-[10px]"
                                        />
                                        <div className="min-w-0 flex-1">
                                            <p className="truncate text-xs font-medium text-foreground">
                                                {report.user?.fullName || "Unknown user"}
                                            </p>
                                            <p className="text-[11px] text-muted-foreground">
                                                {formatReportDate(report.reportDate)}
                                            </p>
                                        </div>
                                        {report.blocker ? (
                                            <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-destructive/30 bg-destructive/10 px-1.5 py-0.5 text-[10px] font-semibold text-destructive">
                                                <AlertCircle className="h-2.5 w-2.5" />
                                                Blocker
                                            </span>
                                        ) : null}
                                    </div>
                                    <p className="mt-1.5 line-clamp-3 text-xs leading-relaxed text-muted-foreground">
                                        {report.content}
                                    </p>
                                    {report.blocker && report.blockedReason ? (
                                        <div className="mt-1.5 rounded border border-destructive/20 bg-destructive/[0.04] px-2 py-1.5">
                                            <p className="text-[10px] font-semibold text-destructive">Blocker details</p>
                                            <p className="mt-0.5 line-clamp-2 text-[10px] text-muted-foreground">
                                                {report.blockedReason}
                                            </p>
                                        </div>
                                    ) : null}
                                </div>
                            ))
                        )
                    ) : (
                        <>
                            <div className="flex gap-2 text-xs text-muted-foreground">
                                <Crown className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" aria-hidden />
                                <span>
                                    {!driAssignment ? (
                                        <>
                                            <span className="font-medium text-foreground">No DRI yet</span> — this stays a
                                            candidate until someone takes it.
                                        </>
                                    ) : driAssignment.userPid === user?.pid ? (
                                        <>
                                            <span className="font-medium text-foreground">You're the DRI</span> — you own
                                            unblocking the team.
                                        </>
                                    ) : (
                                        <>
                                            <span className="font-medium text-foreground">{driDisplayName} is the DRI</span> —
                                            they own unblocking the team.
                                        </>
                                    )}
                                </span>
                            </div>
                            <div className="flex gap-2 text-xs text-muted-foreground">
                                <span className="text-sm text-primary">→</span>
                                <span>
                                    Assignees start in <strong className="font-medium text-foreground">Queue</strong>
                                    {wWeek && wYear ? <> for week {wWeek}, {wYear}</> : null}
                                </span>
                            </div>
                            {notificationNames ? (
                                <div className="flex gap-2 text-xs text-muted-foreground">
                                    <span className="text-sm text-primary">→</span>
                                    <span>
                                        {notificationNames} {notifyFirstNames.length === 1 ? "gets" : "get"} notified in app
                                    </span>
                                </div>
                            ) : null}
                            <div className="flex gap-2 text-xs text-muted-foreground">
                                <span className="text-sm text-primary">→</span>
                                <span>Appears on the team board right away</span>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </aside>
    );
}
